import { type ReactNode, useCallback, useMemo, useState } from 'react';

import {
  type DateOrder,
  type DateSeparator,
  type DateTimePreference,
  type HourCycle,
  writeDateTimePreference,
} from './dateTimePreference.ts';
import { DateTimeFormatContext, type DateTimeFormatContextValue } from './dateTimeFormatContext.ts';

export interface DateTimeFormatProviderProps {
  /** Read from storage by `main.tsx`, matching `ThemeProvider`'s identical `initialPreference` prop
   * and the same reasoning: reading `localStorage` belongs at the entry point, not in a lazy
   * `useState` initialiser, so every component's render stays a pure function of its inputs. */
  readonly initialPreference: DateTimePreference;
  readonly children: ReactNode;
}

/**
 * Holds the date/time display preference and persists changes to `localStorage`.
 *
 * Simpler than `ThemeProvider`: a format preference has no accessibility failure mode a change
 * could trigger (there is no "illegible" date order), so there is nothing here to measure or roll
 * back — every commit just applies and persists.
 */
export function DateTimeFormatProvider({
  initialPreference,
  children,
}: DateTimeFormatProviderProps) {
  const [preference, setPreference] = useState<DateTimePreference>(initialPreference);

  const commit = useCallback((next: DateTimePreference) => {
    writeDateTimePreference(window.localStorage, next);
    setPreference(next);
  }, []);

  const setDateOrder = useCallback(
    (dateOrder: DateOrder) => {
      commit({ ...preference, dateOrder });
    },
    [commit, preference],
  );

  const setSeparator = useCallback(
    (separator: DateSeparator) => {
      commit({ ...preference, separator });
    },
    [commit, preference],
  );

  const setHourCycle = useCallback(
    (hourCycle: HourCycle) => {
      commit({ ...preference, hourCycle });
    },
    [commit, preference],
  );

  const value = useMemo<DateTimeFormatContextValue>(
    () => ({ preference, setDateOrder, setSeparator, setHourCycle }),
    [preference, setDateOrder, setSeparator, setHourCycle],
  );

  return <DateTimeFormatContext.Provider value={value}>{children}</DateTimeFormatContext.Provider>;
}
